/**
 * Bella Christie Jewellery — Recently Viewed Products
 *
 * Tracking: product IDs are saved to localStorage key 'bc_recently_viewed'
 *           by an inline script in layout/theme.liquid on every product page.
 *
 * Rendering: on page load the <recently-viewed-products> custom element:
 *   1. Reads IDs from localStorage
 *   2. Fires a Section Rendering API request:
 *      GET /search?type=product&q=id:X OR id:Y&sections=<sectionId>
 *   3. Parses the returned HTML and replaces its own innerHTML
 *   4. Hides itself if there are no recently viewed items
 */

const BC_RV_KEY = 'bc_recently_viewed';

class RecentlyViewedProducts extends HTMLElement {
  async connectedCallback() {
    // Skip in the theme editor preview (Shopify design mode) to avoid
    // hiding the section while the merchant is customising it.
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    let ids;
    try {
      ids = JSON.parse(localStorage.getItem(BC_RV_KEY) || '[]');
    } catch {
      ids = [];
    }

    // Remove the current product so it doesn't appear in its own "recently viewed"
    const currentProductId = document.querySelector('[data-product-id]')?.dataset?.productId;
    if (currentProductId) {
      ids = ids.filter(id => id !== String(currentProductId));
    }

    const max = parseInt(this.dataset.maxProducts || '6', 10);
    ids = ids.slice(0, max);

    if (ids.length === 0) {
      this.setAttribute('data-has-items', 'false');
      return;
    }

    const sectionId = this.dataset.sectionId;
    const query     = ids.map(id => `id:${id}`).join(' OR ');
    const url       = `/search?type=product&q=${encodeURIComponent(query)}&sections=${sectionId}`;

    try {
      const res  = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const html = data[sectionId];
      if (!html) throw new Error('No section HTML returned');

      // Parse and extract the inner content of <recently-viewed-products>
      const parser  = new DOMParser();
      const doc     = parser.parseFromString(html, 'text/html');
      const newEl   = doc.querySelector('recently-viewed-products');
      if (newEl) {
        this.innerHTML = newEl.innerHTML;
        // Re-initialise any scripts/custom elements in the injected HTML
        this._reinitScripts();
      }
    } catch (err) {
      // Silently hide on any error — no recently viewed is never a hard failure
      console.warn('[recently-viewed] Could not load products:', err);
      this.setAttribute('data-has-items', 'false');
    }
  }

  /**
   * Shopify scripts inside innerHTML don't auto-execute.
   * Re-append them so custom elements (carousels, etc.) initialise correctly.
   */
  _reinitScripts() {
    this.querySelectorAll('script').forEach(oldScript => {
      const newScript = document.createElement('script');
      [...oldScript.attributes].forEach(attr => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
